import { QueryClient } from "@tanstack/react-query";
import { MethodForModel, ModelName, ParamsForMethod, QueryDataForMethod } from "../../types";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for getting query data from cache.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 */
export interface GetTellescopeQueryDataArgs<Model extends ModelName, Method extends MethodForModel<Model>> {
  /** The Tellescope model name */
  model: Model;
  /** The query method to call on the model */
  method: Method;
  /** The request parameters for the query */
  request: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
}

/**
 * Gets cached query data from the query client for a Tellescope query.
 * Returns `undefined` if no cached data exists.
 * For infinite/paginated queries, returns data wrapped in InfiniteData structure.
 *
 * @param args - The get query data arguments
 * @returns The cached data or undefined
 *
 * @example
 * ```tsx
 * // Standard query
 * const user = getTellescopeQueryData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * }); // Returns User | undefined
 *
 * // Infinite/paginated query
 * const users = getTellescopeQueryData({
 *   queryClient,
 *   model: "users",
 *   method: "getSome",
 *   request: { filter: {}, limit: 10 },
 * }); // Returns InfiniteData<User[]> | undefined
 * ```
 */
export const getTellescopeQueryData = <Model extends ModelName, Method extends MethodForModel<Model>>({
  model,
  method,
  request,
  queryClient,
}: GetTellescopeQueryDataArgs<Model, Method>): QueryDataForMethod<Model, Method> | undefined => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });
  return queryClient.getQueryData<QueryDataForMethod<Model, Method>>(queryKey);
};
